import { useChatStoreExtract } from '../../../Chat/hooks/ChatStore.hooks';
import logger from '@scale/frontend-lib/logging';
import _ from 'lodash';
import { useMemo } from 'react';
import { useAsync } from 'react-use-v13';
import {
  CandidateMessageWithMetadata,
  ChatApiEndpointWithId,
  ChatTaskContextType,
  SampleModelParams,
  StepParamsForTypeWithStage,
  InteractionStepType,
  InteractionStepResponseForType,
  ModelResponseSelectorStepResponse,
} from '@scale/shared/types/chat';
import { SampleModelsConfigResponse, SampleModelsResponse } from '@scale/shared/types/rlhf';
import apiFetch from '@scale/frontend-lib/api-fetch';

// Helper function to check if any model has sample model params
export const hasAnySampleModelParams = (chatModels: ChatApiEndpointWithId[]): boolean => {
  return chatModels.some(
    (model: ChatApiEndpointWithId) => model.params?.sampleModelParams?.workstreamName,
  );
};

// Helper function to merge chat model configs
// Merge the existing valid configs and prioritize reusing existing response model params
// over the previous response model params.
export const mergeChatModelConfigs = (
  step: StepParamsForTypeWithStage<InteractionStepType.ModelResponseSelector>,
  chatModelsFromResponse: (ChatApiEndpointWithId | undefined)[],
  chatModelsFromPreviousResponse: (ChatApiEndpointWithId | undefined)[],
): (ChatApiEndpointWithId | undefined)[] => {
  return step.params.chat_models.map(
    (_, i) => chatModelsFromResponse[i] || chatModelsFromPreviousResponse[i],
  );
};

// Helper function to get number of sample model params to fetch by workstream
export const getNumSampleModelParamsToFetchByWorkstream = (
  chatModelConfigs: (ChatApiEndpointWithId | undefined)[],
  step: StepParamsForTypeWithStage<InteractionStepType.ModelResponseSelector>,
): Record<string, number> => {
  // Determine which workstreams we need to sample and how many times
  const numSampleModelParamsToFetchByWorkstream: Record<string, number> = chatModelConfigs.reduce(
    (acc: Record<string, number>, config, i) => {
      // If we already have a valid model for this candidate, do nothing.
      // This is true for non-sampled models and cases where an existing sampled model is still valid.
      if (config) {
        return acc;
      }

      // Sanity check that the model exists
      const model = step.params.chat_models[i];
      if (!model) {
        return acc;
      }

      const workstream = model.params?.sampleModelParams?.workstreamName;
      if (workstream) {
        if (!acc[workstream]) {
          acc[workstream] = 0;
        }
        acc[workstream]++;
      }
      return acc;
    },
    {},
  );

  return numSampleModelParamsToFetchByWorkstream;
};

// Helper function to fetch sampled models
export const fetchSampledModels = async (
  workstreamName: string,
  numModelsNeeded: number,
  taskContext: ChatTaskContextType,
): Promise<SampleModelsResponse[]> => {
  const numSampleModelsCalls = Math.ceil(numModelsNeeded / 2);
  let retryCount = 2;
  while (retryCount > 0) {
    try {
      const sampleModelsResponses: SampleModelsResponse[] = [];
      await Promise.all(
        _.range(numSampleModelsCalls).map(async () => {
          const res = await apiFetch(null, 'internal/self_serve/rlhf/sampleModels', {
            method: 'POST',
            body: JSON.stringify({
              sampleModelParams: { workstreamName },
              assignmentId: taskContext.assignmentId ?? taskContext.taskId,
              projectId: taskContext.projectId,
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            maxRetries: 3,
          });
          sampleModelsResponses.push(...res);
        }),
      );

      return sampleModelsResponses;
    } catch (e) {
      retryCount -= 1;
      logger.error('AsyncModelResponseSelectorTaskComponent - Error on sampling model', {
        e,
        errMessage: e.message,
      });
    }
  }
  return [];
};

export const getValidCandidateIndexes = (
  chatModelConfig: ChatApiEndpointWithId[],
  workstreamModels: Record<string, string[]>,
  response: InteractionStepResponseForType<InteractionStepType.ModelResponseSelector>,
): number[] => {
  if (!response) {
    return [];
  }

  return response.context.candidates.reduce(
    (filtered: number[], candidate: CandidateMessageWithMetadata, index: number) => {
      const sampleModelParams: SampleModelParams | undefined =
        chatModelConfig[index]?.params?.sampleModelParams;

      if (!sampleModelParams) {
        filtered.push(index);
      } else if (
        workstreamModels[sampleModelParams.workstreamName] &&
        workstreamModels[sampleModelParams.workstreamName].includes(
          candidate.endpoint?.params.model,
        )
      ) {
        filtered.push(index);
      }
      return filtered;
    },
    [],
  );
};

export const fetchWorkstreamModels = async (
  chatModels: ChatApiEndpointWithId[],
  taskContext: ChatTaskContextType,
): Promise<Record<string, string[]>> => {
  const workstreamsSet: Set<string> = chatModels.reduce(
    (filtered: Set<string>, model: ChatApiEndpointWithId) => {
      if (model.params?.sampleModelParams?.workstreamName) {
        filtered.add(model.params.sampleModelParams.workstreamName);
      }
      return filtered;
    },
    new Set<string>(),
  );

  if (workstreamsSet.size === 0) {
    return {};
  }

  try {
    const res: SampleModelsConfigResponse = await apiFetch(
      null,
      'internal/self_serve/rlhf/sampleModelsConfig',
      {
        method: 'POST',
        body: JSON.stringify({
          workstreams: Array.from(workstreamsSet.values()),
          assignmentId: taskContext.assignmentId ?? taskContext.taskId,
          projectId: taskContext.projectId,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
        maxRetries: 1,
      },
    );
    return res.workstreamModels;
  } catch (e) {
    logger.error(
      'AsyncModelResponseSelectorTaskComponent - Error fetching sample model config, assuming no models are valid',
      {
        e,
        errMessage: e.message,
      },
    );
    return {};
  }
};

export const useChatModels = ({
  step,
  response,
  previousModelResponse,
  taskContext,
  onlyUseExistingMessages,
}: {
  step: StepParamsForTypeWithStage<InteractionStepType.ModelResponseSelector>;
  response: InteractionStepResponseForType<InteractionStepType.ModelResponseSelector> | undefined;
  previousModelResponse:
    | InteractionStepResponseForType<InteractionStepType.ModelResponseSelector>
    | undefined;
  taskContext: ChatTaskContextType;
  onlyUseExistingMessages: boolean;
}): {
  chatModels: ChatApiEndpointWithId[];
  chatModelsError: string | null;
  chatModelsLoading: boolean;
} => {
  const workstreamModels = useChatStoreExtract(state => state.workstreamModels);
  const setWorkstreamModels = useChatStoreExtract(state => state.setWorkstreamModels);

  const {
    value: chatModels,
    error: chatModelsError,
    loading: chatModelsLoading,
  } = useAsync(async () => {
    if (onlyUseExistingMessages) {
      return [];
    }

    // If we do not need to sample models, just return the configured models
    if (!hasAnySampleModelParams(step.params.chat_models)) {
      return step.params.chat_models.map(model => ({
        ...model,
        params: {
          ...model.params,
          ..._.omitBy(
            {
              // add assignmentId and projectId if we have them available
              assignmentId: taskContext.assignmentId ?? taskContext.taskId,
              projectId: taskContext.projectId,
            },
            _.isNil,
          ),
        },
      }));
    }

    // Fetch the list of valid models
    if (!Object.keys(workstreamModels).length) {
      const fetchedWorkstreamModels = await fetchWorkstreamModels(
        step.params.chat_models,
        taskContext,
      );
      if (Object.keys(fetchedWorkstreamModels).length) {
        setWorkstreamModels(fetchedWorkstreamModels);
      }
    }

    // List of model parameters from an existing response that contains valid models (to be re-used)
    // Ex: [undefined, {...}, undefined] means that the first and third models are no longer valid and
    // we need to re-sample models to fetch a response for those two candidates.
    let chatModelsFromResponse: (ChatApiEndpointWithId | undefined)[] = [];
    let chatModelsFromPreviousResponse: (ChatApiEndpointWithId | undefined)[] = [];

    // If there is an existing response, try to use those params
    if (response?.context?.candidates?.length) {
      const validCandidateIndexes = getValidCandidateIndexes(
        step.params.chat_models,
        workstreamModels || {},
        response,
      );

      const existingModelParamsFromResp = response.context.candidates.map(
        candidate => candidate.endpoint,
      );
      if (validCandidateIndexes.length === response.context.candidates.length) {
        return existingModelParamsFromResp;
      }

      chatModelsFromResponse = existingModelParamsFromResp.map((model, index) =>
        validCandidateIndexes.includes(index) ? model : undefined,
      );
    }

    // filter for the ones that are present in step.params

    const filteredPreviousModelCandidates = _.filter(
      previousModelResponse?.context?.candidates,
      (candidate: any) => _.some(step.params.chat_models, model => model.id === candidate.id),
    );

    const previousModelResponseWithFilteredCandidates = previousModelResponse
      ? _.cloneDeep(previousModelResponse)
      : { context: { candidates: [] } };
    _.set(
      previousModelResponseWithFilteredCandidates,
      'context.candidates',
      filteredPreviousModelCandidates,
    );

    // if we have a previous model response from prior turns, gather the endpoint params from that
    if (previousModelResponseWithFilteredCandidates.context.candidates.length) {
      // Check the validity of the previous model params.
      // This could be different than the current step's params in cases where we re-sampled
      // the previous step, but not the current step.
      const validCandidateIndexes = getValidCandidateIndexes(
        step.params.chat_models,
        workstreamModels || {},
        previousModelResponseWithFilteredCandidates as ModelResponseSelectorStepResponse,
      );
      chatModelsFromPreviousResponse =
        previousModelResponseWithFilteredCandidates.context.candidates.map(
          (candidate: any, index: any) => {
            if (!validCandidateIndexes.includes(index)) {
              return undefined;
            }

            // Remove the sampleModelParams from the params
            const { sampleModelParams, ...restParams } = step.params.chat_models[index].params;
            return {
              ...step.params.chat_models[index],
              params: {
                assignmentId: taskContext.assignmentId ?? taskContext.taskId,
                projectId: taskContext.projectId,
                ...restParams,
                ...candidate.endpoint?.params,
              },
            };
          },
        );
    }

    const chatModelConfigs = mergeChatModelConfigs(
      step,
      chatModelsFromResponse,
      chatModelsFromPreviousResponse,
    );

    // If all of the configs are valid, use them, otherwise, we (re-)sample model params for this response
    if (
      chatModelConfigs.every((config: ChatApiEndpointWithId | undefined) => config !== undefined)
    ) {
      return chatModelConfigs as ChatApiEndpointWithId[];
    }

    const numSampleModelParamsToFetchByWorkstream = getNumSampleModelParamsToFetchByWorkstream(
      chatModelConfigs,
      step,
    );
    // Call the sample models API for each workstream that needs new sampled models
    const sampleModelsResponsesByWorkstream: Record<string, SampleModelsResponse[]> = {};
    for (const [workstreamName, numSampleModelParamsNeeded] of Object.entries(
      numSampleModelParamsToFetchByWorkstream,
    )) {
      const res = await fetchSampledModels(workstreamName, numSampleModelParamsNeeded, taskContext);
      if (res.length) {
        sampleModelsResponsesByWorkstream[workstreamName] = res;
      }
    }

    // Use the newly sampled model params in the current configs where needed or use a fallback
    return step.params.chat_models.map((model: ChatApiEndpointWithId, i: number) => {
      // Use an existing valid config from an existing response instead of a sampled one, if it exists.
      const config = chatModelConfigs[i];
      if (config) {
        return config;
      }

      // Remove the sample model params from the rest of the params
      const { sampleModelParams, fallbackModelParams, ...restParams } = model.params;
      // Sanity check that this chat model uses sample model params
      // (this should already be handled by the first if statement)
      if (!sampleModelParams) {
        return model;
      }

      const sampleModelsResponsesForWorkspace =
        sampleModelsResponsesByWorkstream[sampleModelParams.workstreamName];

      if (sampleModelsResponsesForWorkspace?.length) {
        return {
          ...model,
          params: {
            assignmentId: taskContext.assignmentId ?? taskContext.taskId,
            projectId: taskContext.projectId,
            ...restParams,
            ...sampleModelsResponsesForWorkspace.shift(),
          },
        };
      } else {
        // fallback to the original model params if the sampling fails
        const fallbackConfig = {
          ...model,
          params: {
            usedFallbackParams: true,
            projectId: taskContext.projectId,
            assignmentId: taskContext.assignmentId ?? taskContext.taskId,
            ...restParams,
            ...fallbackModelParams,
          },
        };

        logger.error(
          `AsyncModelResponseSelectorTaskComponent - Error on sampling model, using fallback ${JSON.stringify(
            { ...fallbackConfig },
          )}`,
        );
        return fallbackConfig;
      }
    });
  }, [step.params.chat_models, workstreamModels]);

  const memoizedChatModels = useMemo(() => {
    return (chatModels ?? []).filter(
      (model: ChatApiEndpointWithId | undefined) => model !== undefined,
    ) as ChatApiEndpointWithId[];
  }, [chatModels]);

  return {
    chatModels: memoizedChatModels,
    chatModelsError: chatModelsError?.message ?? null,
    chatModelsLoading,
  };
};
